// The authored feed layout, resolved on the CLIENT.
//
// The engine resolves a feed card server-side for a process trail, where the
// record isn't loaded yet. A record list is the other way round: every row is
// already in memory, so asking the server to lay out each one would be a round
// trip per card for values the client is holding. Same wells, same order, same
// cells — see `process_trail.feed_view()`, which this mirrors.
//
// Only an AUTHORED layout is honoured here. The engine's derived default exists
// so a trail card still reads for a form nobody laid out; a record list already
// has its own defaults, and quietly swapping them for a guess would change what
// every unauthored list looks like.

import type { ColumnDef, SqlModel } from '../types'
import { columnDbName, columnFormKey, visibleColumns } from '../utils'
import type { FeedCardRows, FeedCell } from './contract'

/** Row key ← the Feeds visual's StandardName. Mirrors the engine's WELLS. */
const WELLS: Array<[keyof FeedCardRows, string]> = [
  ['first_left', 'firstrowleft'],
  ['first_right', 'firstrowright'],
  ['second_left', 'secondrowleft'],
  ['second_right', 'secondrowright'],
  ['third_left', 'thirdrowleft'],
  ['third_right', 'thirdrowright'],
]

function authoredWells(sm: SqlModel): Map<keyof FeedCardRows, NonNullable<SqlModel['feed_view']>['visualLayout']> {
  const out = new Map<keyof FeedCardRows, NonNullable<SqlModel['feed_view']>['visualLayout']>()
  const layout = sm.feed_view?.visualLayout || []
  for (const [key, std] of WELLS) {
    const hit = layout.find(
      (g) => String(g?.StandardName || '').trim().toLowerCase() === std)
    const cols = (hit?.COLUMNS || []).filter(Boolean)
    if (cols.length) out.set(key, [{ GroupName: '', StandardName: std, COLUMNS: cols }])
  }
  return out
}

/** True when this form's feed view was actually laid out. */
export function hasFeedView(sm: SqlModel): boolean {
  return authoredWells(sm).size > 0
}

function byDb(sm: SqlModel): Map<string, ColumnDef> {
  const m = new Map<string, ColumnDef>()
  for (const c of visibleColumns(sm)) {
    const db = columnDbName(c)
    if (db) m.set(db.toUpperCase(), c)
  }
  return m
}

/** The row's value for a column. List rows come back keyed by db_name, but a
 *  few key shapes are probed so a name/db_name or casing difference doesn't
 *  silently blank a card. */
function rawValue(row: Record<string, unknown>, col: ColumnDef): unknown {
  const keys = [
    columnDbName(col), columnFormKey(col), col.name, col.db_name,
    (col.name || '').toLowerCase(), (col.db_name || '').toLowerCase(),
  ]
  for (const k of keys) {
    if (!k) continue
    const v = row[k]
    if (v != null && v !== '') return v
  }
  return null
}

/** Every column this form's feed view puts on a card, in well order. What a
 *  list searches over when the feed layout is what it is showing. */
export function feedViewColumns(sm: SqlModel): ColumnDef[] {
  const cols = byDb(sm)
  const out: ColumnDef[] = []
  const seen = new Set<ColumnDef>()
  for (const [key] of WELLS) {
    for (const ref of authoredWells(sm).get(key)?.[0]?.COLUMNS || []) {
      const col = ref.name ? cols.get(ref.name.trim().toUpperCase()) : undefined
      if (col && !seen.has(col)) { out.push(col); seen.add(col) }
    }
  }
  return out
}

/** One record laid out by its form's feed view, or null when there is none. */
export function feedRowsFor(
  sm: SqlModel, row: Record<string, unknown>,
): FeedCardRows | null {
  const wells = authoredWells(sm)
  if (wells.size === 0) return null
  const cols = byDb(sm)
  const rows: FeedCardRows = {}
  for (const [key] of WELLS) {
    const cells: FeedCell[] = []
    for (const ref of wells.get(key)?.[0]?.COLUMNS || []) {
      const label = (ref.label || '').trim() || undefined
      if (ref.constant != null && ref.constant !== '') {
        cells.push({ text: ref.constant })
        continue
      }
      const col = ref.name ? cols.get(ref.name.trim().toUpperCase()) : undefined
      if (!col) continue
      cells.push({ value: rawValue(row, col), col, label })
    }
    if (cells.length) rows[key] = cells
  }
  return Object.keys(rows).length ? rows : null
}
